import { SupportConsole } from "./ServiceSupport";
import { FavoritesAvailability } from "./FavoritesAvailability";
import { CustomerReviewEditor } from "./CustomerBookingTools";
import { type FormEvent, useCallback, useEffect, useRef, useState } from "react";
import {
  Bell,
  Bot,
  CalendarDays,
  CheckCircle2,
  Heart,
  ImagePlus,
  LogOut,
  MessageCircle,
  Search,
  Send,
  ShieldCheck,
  Sparkles,
  Trash2,
  Users,
} from "lucide-react";
import { API_URL, ApiError, api, apiForm, errorMessage, track } from "./api";
import { Turnstile } from "./Turnstile";
import { completeDiscoveryPreferences, discoveryQuery, readDiscoveryPreferences, type DiscoveryCoverage } from "./discovery-query";

const locale = window.location.pathname.split("/")[1] === "en" ? "en" : "ko";
const token = () => localStorage.getItem("hb_access_token") || "";
const navigate = (path: string) => {
  window.history.pushState({}, "", `/${locale}${path}`);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0, behavior: "smooth" });
};
const dateTime = (value: string) =>
  new Intl.DateTimeFormat("ko-KR", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Asia/Seoul",
  }).format(new Date(value));
const won = (value: number) => `${new Intl.NumberFormat("ko-KR").format(value)}원`;
const bookingStatusLabels: Record<string, string> = {
  pending: "확인 기다리는 중",
  confirmed: "예약 확정",
  completed: "시술 완료",
  cancelled: "예약 취소",
  no_show: "방문하지 않음",
  reschedule_requested: "시간 조정 중",
};
const bookingStatusLabel = (status: string) => bookingStatusLabels[status] || "상태 확인 중";

function LoginRequired({ text }: { text: string }) {
  return (
    <main className="platform-page">
      <section className="panel platform-empty">
        <ShieldCheck />
        <h1>로그인이 필요합니다</h1>
        <p>{text}</p>
        <button className="button" onClick={() => navigate("/start")}>
          로그인·가입하기
        </button>
      </section>
    </main>
  );
}

function ConsumerAuth({ onAuthenticated }: { onAuthenticated: (accessToken: string) => void }) {
  const [mode, setMode] = useState<"register" | "login">("register");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [challenge, setChallenge] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [providers, setProviders] = useState({ google: false, naver: false });
  useEffect(() => {
    void api<{ google: boolean; naver: boolean }>("/api/v1/auth/oauth/providers")
      .then(setProviders)
      .catch(() => undefined);
  }, []);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setMessage("");
    const values = new FormData(event.currentTarget);
    try {
      const result = await api<{ accessToken: string }>(`/api/v1/auth/${mode}`, {
        method: "POST",
        body: JSON.stringify(
          mode === "register"
            ? {
                name: values.get("name"),
                email: values.get("email"),
                password: values.get("password"),
                accountType: "customer",
                turnstileToken: challenge || undefined,
              }
            : {
                email: values.get("email"),
                password: values.get("password"),
                turnstileToken: challenge || undefined,
              },
        ),
      });
      localStorage.setItem("hb_access_token", result.accessToken);
      onAuthenticated(result.accessToken);
    } catch (error) {
      setMessage(errorMessage(error));
      setChallenge("");
      setAttempt((value) => value + 1);
    } finally {
      setBusy(false);
    }
  }
  function social(provider: "google" | "naver") {
    window.location.assign(`${API_URL}/api/v1/auth/oauth/${provider}/start?locale=${locale}&accountType=customer`);
  }
  return (
    <main className="platform-page consumer-auth-page">
      <section className="consumer-auth-intro">
        <p className="eyebrow">My HairBridge</p>
        <h1>
          예약한 뒤에도
          <br />내 디자이너와 이어져요.
        </h1>
        <p>
          예약은 가입 없이 할 수 있어요. 계정은 찜, 예약 이력, 검증 후기와 재예약을 여러 기기에서 이어보고 싶을 때만
          만드세요.
        </p>
        <button className="text-button" onClick={() => navigate("/discover")}>
          먼저 디자이너 찾아보기
        </button>
      </section>
      <form className="panel consumer-auth-form" onSubmit={submit}>
        <div className="segmented">
          <button type="button" className={mode === "register" ? "active" : ""} onClick={() => setMode("register")}>
            계정 만들기
          </button>
          <button type="button" className={mode === "login" ? "active" : ""} onClick={() => setMode("login")}>
            로그인
          </button>
        </div>
        <h2>{mode === "register" ? "내 예약 이어보기" : "다시 오셨군요"}</h2>
        <div className="social-login-grid">
          <button
            type="button"
            className="social-login google"
            disabled={!providers.google}
            onClick={() => social("google")}
          >
            <span>G</span> Google로 계속
          </button>
          <button
            type="button"
            className="social-login naver"
            disabled={!providers.naver}
            onClick={() => social("naver")}
          >
            <span>N</span> {providers.naver ? "네이버로 계속" : "네이버 준비 중"}
          </button>
        </div>
        {mode === "register" && (
          <label>
            이름
            <input name="name" minLength={2} maxLength={80} autoComplete="name" required />
          </label>
        )}
        <label>
          이메일
          <input name="email" type="email" autoComplete="email" required />
        </label>
        <label>
          비밀번호
          <input
            name="password"
            type="password"
            minLength={mode === "register" ? 10 : 1}
            autoComplete={mode === "register" ? "new-password" : "current-password"}
            required
          />
        </label>
        <Turnstile key={attempt} onToken={setChallenge} />
        {message && (
          <p className="platform-message" role="alert">
            {message}
          </p>
        )}
        <button className="button button-full" disabled={busy}>
          {busy ? "확인 중…" : mode === "register" ? "선택 계정 만들기" : "로그인"}
        </button>
        <small>계정 없이도 예약·관리 링크·후기 작성은 계속 사용할 수 있어요.</small>
      </form>
    </main>
  );
}

interface Favorite {
  user_id: string;
  name: string;
  slug: string;
  salon_name: string | null;
  min_price: number | null;
  rating: number | null;
  review_count: number;
}
interface AccountBooking {
  id: string;
  scheduled_at: string;
  status: string;
  customer_name: string;
  service_name: string;
  price: number;
  slug: string;
  salon_name: string | null;
  designer_name: string;
  review_id: string | null;
  review_rating: number | null;
  review_comment: string | null;
  review_status: "published" | "under_review" | "hidden" | null;
  unread_messages: number;
}
interface Notification {
  id: string;
  kind: string;
  title: string;
  body: string;
  link: string | null;
  read_at: string | null;
  created_at: string;
}
interface RebookingCandidate {
  id: string;
  due_at: string;
  interval_weeks: number;
  status: "pending" | "snoozed";
  notified_at: string | null;
  service_name: string;
  slug: string;
  salon_name: string | null;
  designer_name: string;
}
interface AccountOverview {
  user: { id: string; name: string; email: string };
  favorites: Favorite[];
  bookings: AccountBooking[];
  rebooking: RebookingCandidate[];
  notifications: Notification[];
  unreadNotifications: number;
}

export function AccountPage() {
  const [accessToken, setAccessToken] = useState(token);
  const [data, setData] = useState<AccountOverview | null>(null);
  const [message, setMessage] = useState("");
  const [openChat, setOpenChat] = useState("");
  const [oauthBusy, setOauthBusy] = useState(false);
  const load = useCallback(() => {
    if (!accessToken) return;
    void api<AccountOverview>("/api/v1/account/overview", {}, accessToken)
      .then(setData)
      .catch((error) => setMessage(errorMessage(error)));
  }, [accessToken]);
  useEffect(load, [load]);
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.slice(1));
    const code = params.get("oauth_code");
    const oauthError = params.get("oauth_error");
    if (!code && !oauthError) return;
    window.history.replaceState({}, "", window.location.pathname + window.location.search);
    if (oauthError) {
      setMessage(errorMessage(new ApiError(oauthError, 400)));
      return;
    }
    setOauthBusy(true);
    void api<{ accessToken: string }>("/api/v1/auth/oauth/exchange", {
      method: "POST",
      body: JSON.stringify({ code }),
    })
      .then((result) => {
        localStorage.setItem("hb_access_token", result.accessToken);
        setAccessToken(result.accessToken);
      })
      .catch((error) => setMessage(errorMessage(error)))
      .finally(() => setOauthBusy(false));
  }, []);
  useEffect(() => {
    const pendingToken = localStorage.getItem("hb_pending_booking_token");
    if (!accessToken || !pendingToken) return;
    void api(
      "/api/v1/account/bookings/claim",
      {
        method: "POST",
        body: JSON.stringify({ managementToken: pendingToken }),
      },
      accessToken,
    )
      .then(() => {
        localStorage.removeItem("hb_pending_booking_token");
        setMessage("방금 예약을 My HairBridge에 연결했어요.");
        track("account_claimed", { source: "post_booking" });
        load();
      })
      .catch((error) => setMessage(errorMessage(error)));
  }, [accessToken, load]);
  if (oauthBusy)
    return (
      <main className="platform-page">
        <section className="panel platform-empty">로그인을 마무리하는 중이에요…</section>
      </main>
    );
  if (!accessToken) return <ConsumerAuth onAuthenticated={setAccessToken} />;

  async function claim(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const managementToken = String(new FormData(form).get("managementToken") || "");
    try {
      await api(
        "/api/v1/account/bookings/claim",
        { method: "POST", body: JSON.stringify({ managementToken }) },
        accessToken,
      );
      form.reset();
      setMessage("게스트 예약을 내 계정으로 가져왔습니다.");
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function cancelBooking(id: string) {
    try {
      await api(`/api/v1/account/bookings/${id}`, { method: "DELETE" }, accessToken);
      setMessage("예약을 취소했습니다.");
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function recordBookingLifecycle(id: string, action: "confirmed" | "reschedule_requested") {
    try {
      await api(
        `/api/v1/account/bookings/${id}/lifecycle`,
        { method: "POST", body: JSON.stringify({ action }) },
        accessToken,
      );
      setMessage(
        action === "confirmed"
          ? "예약 시간을 확인했다고 전했습니다."
          : "시간 조정 요청을 전했습니다. 예약 시간은 자동으로 바뀌지 않습니다.",
      );
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function submitReview(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    try {
      await api(
        `/api/v1/account/bookings/${id}/review`,
        {
          method: "POST",
          body: JSON.stringify({
            rating: Number(values.get("rating")),
            comment: values.get("comment"),
          }),
        },
        accessToken,
      );
      setMessage("후기를 등록했습니다.");
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function reportReview(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = new FormData(form);
    try {
      await api(
        `/api/v1/account/reviews/${id}/report`,
        {
          method: "POST",
          body: JSON.stringify({
            reason: values.get("reason"),
            detail: values.get("detail"),
          }),
        },
        accessToken,
      );
      form.reset();
      setMessage("후기를 비공개 검토로 전환하고 운영자에게 전달했습니다.");
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function updateRebooking(id: string, action: "dismiss" | "snooze" | "unsubscribe") {
    try {
      await api(
        `/api/v1/account/rebooking-candidates/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            action,
            ...(action === "snooze" ? { snoozeWeeks: 2 } : {}),
          }),
        },
        accessToken,
      );
      setMessage(
        action === "snooze"
          ? "2주 뒤에 다시 볼 수 있도록 미뤘습니다."
          : action === "unsubscribe"
            ? "이 디자이너의 다음 방문 안내를 껐어요."
            : "이번 다음 방문 안내를 숨겼어요.",
      );
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function readAll() {
    try {
      await api("/api/v1/account/notifications/read-all", { method: "PATCH" }, accessToken);
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function openNotification(item: Notification) {
    try {
      if (!item.read_at) await api(`/api/v1/account/notifications/${item.id}/read`, { method: "PATCH" }, accessToken);
      if (item.link) navigate(item.link);
      else load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function exportAccount() {
    setMessage("");
    try {
      const response = await fetch(`${API_URL}/api/v1/privacy/account-export`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new ApiError("account_export_failed", response.status);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "hairbridge-my-account.json";
      link.click();
      URL.revokeObjectURL(url);
      setMessage("내 계정 데이터를 내려받았어요.");
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function requestDeletion(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!window.confirm("14일 유예 뒤 계정 삭제 대상으로 전환할까요?")) return;
    const form = event.currentTarget;
    try {
      await api(
        "/api/v1/privacy/account-deletion-request",
        {
          method: "POST",
          body: JSON.stringify({
            password: new FormData(form).get("password"),
            confirmation: "DELETE MY HAIRBRIDGE ACCOUNT",
          }),
        },
        accessToken,
      );
      form.reset();
      setMessage("계정 삭제 유예를 시작했어요. 유예 기간에는 요청을 취소할 수 있어요.");
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  const bookings = data?.bookings || [];
  const activeBookings = bookings.filter((booking) => !["completed", "cancelled", "no_show"].includes(booking.status));
  const nextBooking = [...activeBookings].sort(
    (left, right) => new Date(left.scheduled_at).getTime() - new Date(right.scheduled_at).getTime(),
  )[0];
  return (
    <main className="platform-page account-page">
      <header className="account-ledger-header">
        <p className="account-ledger-mark">나와 디자이너를 잇는 기록</p>
        <h1>{data?.user.name ? `${data.user.name}님의 헤어 기록` : "내 헤어 기록"}</h1>
        <p className="account-ledger-intro">
          {nextBooking
            ? `${dateTime(nextBooking.scheduled_at)}에 ${nextBooking.designer_name} 디자이너와 만나요.`
            : "예약한 시간과 지난 시술, 다시 만나고 싶은 디자이너를 한 흐름으로 이어보세요."}
        </p>
        <div className="button-row">
          <button className="button" onClick={() => navigate("/discover")}>
            <Search /> 작업과 가능한 시간 보기
          </button>
          <button
            className="account-logout"
            onClick={() => {
              localStorage.removeItem("hb_access_token");
              setData(null);
              setAccessToken("");
            }}
          >
            <LogOut /> 로그아웃
          </button>
        </div>
      </header>
      {message && (
        <p className="platform-message account-status" role="status">
          {message}
        </p>
      )}
      <div className="account-ledger-layout">
        <section className="account-visit-ledger" aria-labelledby="account-visits-heading">
          <header className="account-ledger-section-heading">
            <div>
              <span>방문 기록</span>
              <h2 id="account-visits-heading">예약과 시술</h2>
            </div>
            <strong>{bookings.length}건</strong>
          </header>
          {bookings.length ? (
            <div className="account-visit-list">
              {bookings.map((booking) => (
                <article key={booking.id} className={`account-visit-entry account-visit-${booking.status}`}>
                  <div className="account-visit-copy">
                    <time dateTime={booking.scheduled_at}>{dateTime(booking.scheduled_at)}</time>
                    <p className="account-visit-status">{bookingStatusLabel(booking.status)}</p>
                    <h3>{booking.salon_name || booking.designer_name}</h3>
                    <p>
                      {booking.service_name} · {booking.designer_name} 디자이너
                    </p>
                  </div>
                  <div className="account-visit-actions">
                    <button
                      className="button button-outline"
                      onClick={() => setOpenChat(openChat === booking.id ? "" : booking.id)}
                    >
                      <MessageCircle /> 예약 대화
                      {booking.unread_messages ? ` ${booking.unread_messages}개` : ""}
                    </button>
                    {booking.status === "confirmed" && (
                      <>
                        <button className="button" onClick={() => void recordBookingLifecycle(booking.id, "confirmed")}>
                          예약 시간 확인
                        </button>
                        <button
                          className="button button-outline"
                          onClick={() => void recordBookingLifecycle(booking.id, "reschedule_requested")}
                        >
                          시간 바꾸기
                        </button>
                      </>
                    )}
                    {!["cancelled", "completed", "no_show"].includes(booking.status) && (
                      <button className="text-button danger" onClick={() => void cancelBooking(booking.id)}>
                        예약 취소
                      </button>
                    )}
                  </div>
                  {openChat === booking.id && <AccountChat bookingId={booking.id} accessToken={accessToken} />}
                  {booking.status === "completed" && !booking.review_rating && (
                    <form className="inline-review" onSubmit={(event) => void submitReview(event, booking.id)}>
                      <label>
                        만족도
                        <select name="rating" defaultValue="5">
                          <option value="5">5점</option>
                          <option value="4">4점</option>
                          <option value="3">3점</option>
                          <option value="2">2점</option>
                          <option value="1">1점</option>
                        </select>
                      </label>
                      <label>
                        후기
                        <input name="comment" maxLength={1000} placeholder="시술 경험을 알려 주세요" />
                      </label>
                      <button className="button">후기 남기기</button>
                    </form>
                  )}
                  {booking.review_id&&<CustomerReviewEditor path={`/api/v1/account/reviews/${booking.review_id}`} token={accessToken} rating={booking.review_rating||5} comment={booking.review_comment||""} onChanged={async()=>{load();}}/>}
                  {booking.review_id && (
                    <details className="review-recovery">
                      <summary>
                        내가 쓴 후기 ·{" "}
                        {booking.review_status === "published"
                          ? "공개 중"
                          : booking.review_status === "under_review"
                            ? "검토 중"
                            : "비공개"}
                      </summary>
                      <p>{booking.review_comment || `${booking.review_rating || 0}점 후기`}</p>
                      {booking.review_status === "published" && (
                        <form onSubmit={(event) => void reportReview(event, booking.review_id!)}>
                          <select name="reason" defaultValue="privacy">
                            <option value="privacy">개인정보가 포함됐어요</option>
                            <option value="false_information">작성 내용이 잘못됐어요</option>
                            <option value="abuse">부적절한 표현이 있어요</option>
                            <option value="other">그 밖의 문제</option>
                          </select>
                          <input name="detail" maxLength={1000} placeholder="운영자가 확인할 내용을 적어 주세요" />
                          <button className="button button-outline">비공개 검토 요청</button>
                        </form>
                      )}
                    </details>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div className="account-primary-empty">
              <span aria-hidden="true">첫</span>
              <div>
                <h3>첫 방문을 기록해 보세요</h3>
                <p>예약하면 시간, 디자이너와의 대화, 시술 후기가 이곳에 이어집니다.</p>
                <button className="button" onClick={() => navigate("/discover")}>
                  내 스타일에 맞는 디자이너 찾기
                </button>
              </div>
            </div>
          )}

          <details className="account-claim-disclosure">
            <summary>로그인 전에 만든 예약 연결하기</summary>
            <p>예약할 때 받은 관리 번호를 입력하면 이 계정의 방문 기록에 더합니다.</p>
            <form className="claim-form" onSubmit={claim}>
              <label>
                예약 관리 번호
                <input name="managementToken" minLength={32} autoComplete="off" required />
              </label>
              <button className="button">예약 연결</button>
            </form>
          </details>

          <section className="account-return-section" aria-labelledby="account-return-heading">
            <header className="account-ledger-section-heading compact">
              <div>
                <span>다시 만날 때</span>
                <h2 id="account-return-heading">다음 시술 이어가기</h2>
              </div>
            </header>
            <p className="account-section-note">마친 시술의 일반적인 관리 주기를 바탕으로만 알려드려요.</p>
            {data?.rebooking.length ? (
              <div className="account-return-list">
                {data.rebooking.map((candidate) => (
                  <article key={candidate.id} className="account-return-entry">
                    <div>
                      <time dateTime={candidate.due_at}>{dateTime(candidate.due_at)}부터</time>
                      <h3>{candidate.salon_name || candidate.designer_name}</h3>
                      <p>
                        {candidate.service_name} · 지난 시술 뒤 약 {candidate.interval_weeks}주
                      </p>
                    </div>
                    <div className="account-visit-actions">
                      <button className="button" onClick={() => navigate(`/designer/${candidate.slug}`)}>
                        가능한 시간 보기
                      </button>
                      <button
                        className="button button-outline"
                        onClick={() => void updateRebooking(candidate.id, "snooze")}
                      >
                        2주 뒤 다시 보기
                      </button>
                      <button className="text-button" onClick={() => void updateRebooking(candidate.id, "dismiss")}>
                        이번 안내 숨기기
                      </button>
                      <button className="text-button" onClick={() => void updateRebooking(candidate.id, "unsubscribe")}>
                        이 디자이너 안내 끄기
                      </button>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <p className="account-quiet-empty">지난 시술이 생기면 알맞은 시기에 여기서 다시 이어드릴게요.</p>
            )}
          </section>
        </section>

        <aside className="account-side-notes" aria-label="찜과 알림">
          <section aria-labelledby="account-favorites-heading">
            <header className="account-side-heading">
              <span>
                <Heart aria-hidden="true" /> 이어갈 사람
              </span>
              <strong>{data?.favorites.length || 0}명</strong>
            </header>
            <h2 id="account-favorites-heading">찜한 디자이너</h2>
            {data&&<FavoritesAvailability favorites={data.favorites} navigate={navigate}/>}
            <div className="favorite-account-list">
              {data?.favorites.length ? (
                data.favorites.map((item) => (
                  <button key={item.user_id} onClick={() => navigate(`/designer/${item.slug}`)}>
                    <span>
                      <b>{item.salon_name || item.name}</b>
                      <small>
                        {item.review_count ? `후기 ${item.review_count}개 · ${item.rating}점` : "첫 후기를 기다리는 중"}
                      </small>
                    </span>
                    <strong>{item.min_price === null ? "가격 문의" : `${won(item.min_price)}부터`}</strong>
                  </button>
                ))
              ) : (
                <div className="account-side-empty">
                  <p>다시 찾고 싶은 디자이너를 저장해 두세요.</p>
                  <button className="text-button" onClick={() => navigate("/discover")}>
                    디자이너 둘러보기
                  </button>
                </div>
              )}
            </div>
          </section>

          <section aria-labelledby="account-notifications-heading">
            <header className="account-side-heading">
              <span>
                <Bell aria-hidden="true" /> 새 소식
              </span>
              <strong>{data?.unreadNotifications || 0}개</strong>
            </header>
            <div className="account-side-title-row">
              <h2 id="account-notifications-heading">알림</h2>
              {!!data?.notifications.length && (
                <button className="text-button" onClick={() => void readAll()}>
                  모두 읽기
                </button>
              )}
            </div>
            <div className="notification-list">
              {data?.notifications.length ? (
                data.notifications.map((item) => (
                  <button
                    key={item.id}
                    className={item.read_at ? "" : "unread"}
                    onClick={() => void openNotification(item)}
                  >
                    <b>{item.title}</b>
                    <span>{item.body}</span>
                    <small>{dateTime(item.created_at)}</small>
                  </button>
                ))
              ) : (
                <p className="account-side-empty">예약이나 대화에 변화가 생기면 이곳에 알려드려요.</p>
              )}
            </div>
          </section>
        </aside>
      </div>

      <section className="account-data-section" aria-labelledby="account-data-heading">
        <div>
          <span>내 정보 선택권</span>
          <h2 id="account-data-heading">보관된 데이터</h2>
          <p>내 계정에 저장된 정보를 직접 내려받거나, 14일의 되돌릴 시간을 두고 삭제할 수 있어요.</p>
        </div>
        <div className="account-data-actions">
          <button className="button button-outline" onClick={() => void exportAccount()}>
            내 데이터 내려받기
          </button>
          <details>
            <summary>계정 삭제 요청</summary>
            <p>요청 뒤 14일 동안 로그인하면 삭제를 취소할 수 있습니다.</p>
            <form onSubmit={requestDeletion}>
              <label>
                현재 비밀번호
                <input name="password" type="password" autoComplete="current-password" minLength={12} required />
              </label>
              <button className="text-button danger">14일 유예 삭제 요청</button>
            </form>
          </details>
        </div>
      </section>
    </main>
  );
}

interface ChatItem {
  id: string;
  sender_type: "designer" | "customer";
  content: string;
  created_at: string;
}
function AccountChat({ bookingId, accessToken }: { bookingId: string; accessToken: string }) {
  const [items, setItems] = useState<ChatItem[]>([]);
  const [message, setMessage] = useState("");
  const endpoint = `/api/v1/chat/customer/bookings/${bookingId}`;
  const load = useCallback(
    () =>
      void api<ChatItem[]>(endpoint, {}, accessToken)
        .then(setItems)
        .catch((error) => setMessage(errorMessage(error))),
    [endpoint, accessToken],
  );
  useEffect(() => {
    load();
    const timer = window.setInterval(load, 8000);
    return () => clearInterval(timer);
  }, [load]);
  async function send(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const content = new FormData(form).get("content");
    try {
      await api(endpoint, { method: "POST", body: JSON.stringify({ content }) }, accessToken);
      form.reset();
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  return (
    <div className="account-chat">
      <div className="chat-messages">
        {items.map((item) => (
          <div key={item.id} className={item.sender_type === "customer" ? "mine" : "theirs"}>
            <p>{item.content}</p>
            <small>{dateTime(item.created_at)}</small>
          </div>
        ))}
      </div>
      {message && <p className="platform-message">{message}</p>}
      <form onSubmit={send}>
        <input name="content" maxLength={2000} placeholder="예약 관련 메시지" required />
        <button className="button">
          <Send /> 보내기
        </button>
      </form>
    </div>
  );
}

interface PortfolioItem {
  id: string;
  title: string;
  alt_text: string;
  asset_path: string;
  service_id: string | null;
  style_tags: string;
  is_featured?:number; sort_order?:number; pair_group_id?:string; pair_role?:string;
  is_published: number;
  created_at: string;
}
export function DashboardPlatformExtras({
  accessToken,
  services,
}: {
  accessToken: string;
  services: Array<{ id: string; name: string }>;
}) {
  const [portfolio, setPortfolio] = useState<PortfolioItem[]>([]);
  const [message, setMessage] = useState("");
  const load = useCallback(
    () =>
      void api<PortfolioItem[]>("/api/v1/portfolio/me", {}, accessToken)
        .then(setPortfolio)
        .catch((error) => setMessage(errorMessage(error))),
    [accessToken],
  );
  useEffect(load, [load]);
  async function upload(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const body = new FormData(form);
    try {
      const input = form.elements.namedItem("file") as HTMLInputElement;
      const files = Array.from(input.files || []).slice(0,10);
      let saved = 0; const failed: File[] = [];
      const pair = body.get("pairRole") === "pair" ? crypto.randomUUID() : "";
      for (let i=0;i<files.length;i++) {
        const file = files[i]; if (!file) continue;
        const data = new FormData(form); data.set("file", file); data.set("sortOrder",String(i));
        if (pair && i < 2) {data.set("pairGroupId", pair); data.set("pairRole", i === 0 ? "before" : "after");} else data.delete("pairRole");
        data.set("clientConsentAttested", body.get("clientConsentAttested") ? "true" : "false");
        try { await apiForm("/api/v1/portfolio/me",data,accessToken); saved++; setMessage(`${saved}/${files.length}장 올렸어요.`); } catch { failed.push(file); }
      }
      if (!failed.length) form.reset();
      setMessage(failed.length ? `${saved}장은 저장했어요 · ${failed.length}장은 올리지 못했어요. 실패한 파일을 다시 골라 주세요.` : `${saved}장을 올렸어요.`);
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function remove(id: string) {
    try {
      await api(`/api/v1/portfolio/me/${id}`, { method: "DELETE" }, accessToken);
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function edit(event: FormEvent<HTMLFormElement>, id: string) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      await api(
        `/api/v1/portfolio/me/${id}`,
        {
          method: "PATCH",
          body: JSON.stringify({
            title: data.get("title"),
            altText: data.get("altText"),
            serviceId: data.get("serviceId") || null,
            styleTags: String(data.get("styleTags") || "")
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean),
            isPublished: data.get("isPublished") === "true",
            isFeatured: data.get("isFeatured") === "on",
            sortOrder: Number(data.get("sortOrder") || 0),
          }),
        },
        accessToken,
      );
      setMessage("포트폴리오 정보를 저장했습니다.");
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  return (
    <section className="dashboard-platform">
      {message && <p className="platform-message">{message}</p>}
      <div className="platform-grid portfolio-dashboard-grid">
        <form className="panel portfolio-upload" onSubmit={upload}>
          <div className="panel-title">
            <span>
              <h2>
                <ImagePlus /> 포트폴리오
              </h2>
              <p>JPG·PNG·WebP, 최대 5MB</p>
            </span>
          </div>
          <input
            name="file"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            aria-label="포트폴리오 이미지 파일"
            multiple
            required
          />
          <select name="serviceId" aria-label="연결할 실제 시술" required>
            <option value="">연결할 시술 선택</option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>
                {service.name}
              </option>
            ))}
          </select>
          <input name="styleTags" maxLength={200} placeholder="스타일 태그, 쉼표로 구분" aria-label="스타일 태그" />
          <input name="title" maxLength={100} placeholder="스타일 이름" aria-label="포트폴리오 스타일 이름" required />
          <input
            name="altText"
            maxLength={200}
            placeholder="이미지 설명(접근성)"
            aria-label="포트폴리오 이미지 설명"
            required
          />
          <label className="field"><span>사진 종류</span><select name="pairRole"><option value="">단독 작업</option><option value="pair">앞의 두 장을 전·후 사진으로</option></select></label>
          <label className="consent"><input name="clientConsentAttested" type="checkbox" required /><span>손님에게 사진 게시 동의를 받았어요.</span></label>
          <input name="isPublished" type="hidden" value="true" />
          <button className="button">
            <ImagePlus /> 이미지 추가
          </button>
        </form>
      </div>
      {portfolio.length > 0 && (
        <div className="portfolio-owner-grid">
          {portfolio.map((item) => (
            <figure key={item.id}>
              <img src={`${API_URL}${item.asset_path}`} alt={item.alt_text} />
              <figcaption>
                <form className="portfolio-edit" onSubmit={(event) => void edit(event, item.id)}>
                  <label><input name="isFeatured" type="checkbox" defaultChecked={Boolean(item.is_featured)} />대표 작업</label><label>순서<input name="sortOrder" type="number" min={0} max={1000} defaultValue={item.sort_order || 0} /></label>
                  <input name="title" defaultValue={item.title} maxLength={100} aria-label="포트폴리오 제목" required />
                  <select name="serviceId" defaultValue={item.service_id || ""} aria-label="연결 시술">
                    <option value="">시술 연결 안 함</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                  <input
                    name="styleTags"
                    defaultValue={(() => {
                      try {
                        const tags = JSON.parse(item.style_tags || "[]");
                        return Array.isArray(tags) ? tags.join(", ") : "";
                      } catch {
                        return "";
                      }
                    })()}
                    maxLength={200}
                    aria-label="스타일 태그"
                  />
                  <input
                    name="altText"
                    defaultValue={item.alt_text}
                    maxLength={200}
                    aria-label="포트폴리오 이미지 설명"
                    required
                  />
                  <select
                    name="isPublished"
                    defaultValue={item.is_published ? "true" : "false"}
                    aria-label="포트폴리오 공개 상태"
                  >
                    <option value="true">공개</option>
                    <option value="false">비공개</option>
                  </select>
                  <button className="text-button">저장</button>
                </form>
                <button type="button" aria-label="포트폴리오 삭제" onClick={() => void remove(item.id)}>
                  <Trash2 />
                </button>
              </figcaption>
            </figure>
          ))}
        </div>
      )}
    </section>
  );
}

interface Recommendation {
  user_id: string;
  name: string;
  slug: string;
  salon_name: string | null;
  bio: string | null;
  region_label: string | null;
  specialties: string[];
  min_price: number | null;
  price_max: number | null;
  rating: number | null;
  review_count: number;
  score: number;
  reasons: string[];
  service_names: string;
  earliest_slot: string;
  matchedService: {
    id: string;
    name: string;
    description: string | null;
    price: number;
    priceMax: number;
    durationMinutes: number;
    portfolioId: string | null;
  };
}
export function MatchingPage() {
  const formRef = useRef<HTMLFormElement>(null);
  const [coverage, setCoverage] = useState<DiscoveryCoverage | null>(null);
  const [coverageError, setCoverageError] = useState(false);
  const [coverageAttempt, setCoverageAttempt] = useState(0);
  const [checkedAt, setCheckedAt] = useState("");
  const [retry, setRetry] = useState(0);
  const [items, setItems] = useState<Recommendation[]>([]);
  const [message, setMessage] = useState("");
  const [searched, setSearched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [favoriteSlugs, setFavoriteSlugs] = useState<string[]>(() => {
    try {
      const value = JSON.parse(localStorage.getItem("hb_favorite_designers") || "[]");
      return Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];
    } catch {
      return [];
    }
  });
  const today = new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
  const [preferences, setPreferences] = useState(() => readDiscoveryPreferences(window.location.search, today));
  const [submitted, setSubmitted] = useState<string | null>(() => {
    const initial = readDiscoveryPreferences(window.location.search, today);
    return completeDiscoveryPreferences(initial) ? discoveryQuery(initial) : null;
  });
  useEffect(() => track("discovery_view", { route: "/discover" }), []);
  useEffect(() => {
    const restore = () => {
      const restored = readDiscoveryPreferences(window.location.search, today);
      setPreferences(restored);
      setSubmitted(completeDiscoveryPreferences(restored) ? discoveryQuery(restored) : null);
    };
    window.addEventListener("popstate", restore);
    return () => window.removeEventListener("popstate", restore);
  }, [today]);
  useEffect(() => {
    const controller = new AbortController();
    setCoverageError(false);
    void api<DiscoveryCoverage>("/api/v1/matching/coverage", {
      signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10_000)]),
    }).then((value) => { if (!controller.signal.aborted) setCoverage(value); })
      .catch(() => { if (!controller.signal.aborted) setCoverageError(true); });
    return () => controller.abort();
  }, [coverageAttempt]);
  useEffect(() => {
    setItems([]);
    setSearched(false);
    setMessage("");
    setCheckedAt("");
    if (!submitted) { setBusy(false); return; }
    const controller = new AbortController();
    let active = true;
    setBusy(true);
    void api<{ recommendations: Recommendation[]; checkedAt?: string }>(
      `/api/v1/matching/recommendations?${submitted}`,
      { signal: AbortSignal.any([controller.signal, AbortSignal.timeout(10_000)]) },
    ).then((result) => {
      if (!active) return;
      setItems(result.recommendations);
      setSearched(true);
      setCheckedAt(result.checkedAt || new Date().toISOString());
      const params = new URLSearchParams(submitted);
      track("discovery_search", { service: params.get("service"), region: params.get("region"),
        date: params.get("date"), results: result.recommendations.length });
    }).catch((error) => { if (active) setMessage(errorMessage(error)); })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; controller.abort(); };
  }, [submitted, retry]);
  function search(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const next = discoveryQuery(preferences);
    if (window.location.search.slice(1) !== next) window.history.pushState({}, "", `${window.location.pathname}?${next}`);
    if (next === submitted) setRetry((value) => value + 1);
    else setSubmitted(next);
  }
  function editConditions() {
    formRef.current?.querySelector<HTMLInputElement>('input[name="region"]')?.focus();
    formRef.current?.scrollIntoView({ block: "center", behavior: "auto" });
  }
  const currentSearch = submitted ? readDiscoveryPreferences(submitted, today) : null;
  async function toggleFavorite(item: Recommendation) {
    const wasFavorite = favoriteSlugs.includes(item.slug);
    const next = wasFavorite ? favoriteSlugs.filter((slug) => slug !== item.slug) : [...favoriteSlugs, item.slug];
    setFavoriteSlugs(next);
    localStorage.setItem("hb_favorite_designers", JSON.stringify(next));
    const accessToken = token();
    if (!accessToken) return;
    try {
      await api(
        `/api/v1/account/favorites/${encodeURIComponent(item.user_id)}`,
        { method: wasFavorite ? "DELETE" : "PUT" },
        accessToken,
      );
      track("favorite_changed", {
        designerId: item.user_id,
        favorited: !wasFavorite,
      });
    } catch (error) {
      setFavoriteSlugs(favoriteSlugs);
      localStorage.setItem("hb_favorite_designers", JSON.stringify(favoriteSlugs));
      setMessage(errorMessage(error));
    }
  }
  return (
    <main className="platform-page matching-page">
      <section className="platform-hero">
        <p className="eyebrow">실제로 예약 가능한 사람</p>
        <h1>
          원하는 시술을 잘하는
          <br />
          개인 디자이너를 찾아보세요.
        </h1>
        <p>
          광고비 순서가 아니에요. 실제 작업, 공개 가격, 완료 예약 후기와 선택한 날짜의 빈 시간으로 이유를 보여드려요.
        </p>
      </section>
      <form ref={formRef} className="panel matching-form" onSubmit={search}>
        <label>
          지역
          <input name="region" value={preferences.region} onChange={(e) => setPreferences({ ...preferences, region: e.target.value })} placeholder="예: 성수, 합정 (선택)" maxLength={80} />
        </label>
        <label>
          원하는 시술
          <input name="service" value={preferences.service} onChange={(e) => setPreferences({ ...preferences, service: e.target.value })} placeholder="예: 레이어드 커트" required maxLength={100} />
        </label>
        <label>
          방문 날짜
          <input name="date" type="date" min={today} value={preferences.date} onChange={(e) => setPreferences({ ...preferences, date: e.target.value })} required />
        </label>
        <button className="button" type="submit">
          <Search aria-hidden="true" /> {busy ? "조건 바꿔 다시 검색" : "가능한 디자이너 보기"}
        </button>
        <details className="matching-options" open={Boolean(preferences.budget || preferences.priority !== "balanced") || undefined}>
          <summary>예산·정렬 조건 (선택)</summary>
          <div>
            <label>예산 (선택)<input name="budget" type="number" min="0" max="10000000" step="1000" value={preferences.budget} onChange={(e) => setPreferences({ ...preferences, budget: e.target.value })} placeholder="예: 70000" /></label>
            <label>가장 중요한 것<select name="priority" value={preferences.priority} onChange={(e) => setPreferences({ ...preferences, priority: e.target.value })}>
              <option value="balanced">균형</option><option value="availability">가능한 시간</option><option value="rating">후기</option><option value="price">가격</option><option value="experience">완료 시술</option>
            </select></label>
          </div>
        </details>
      </form>
      <p className="matching-disclosure">
        결과가 없으면 조건을 자동으로 넓히지 않아요. 지역·시술·날짜를 직접 바꿔 다시 확인할 수 있어요.
      </p>
      {message && (
        <p className="platform-message" role="alert">
          {message}
        </p>
      )}
      {coverage && (
        <aside className="matching-coverage" aria-label="현재 공개 등록 범위">
          <p><b>공개 등록 범위</b> · 디자이너 {coverage.designerCount}명 · 시술 {coverage.serviceCount}개</p>
          <p>등록 지역 예: {coverage.regions.length ? coverage.regions.join(" · ") : "아직 등록된 지역이 없어요."}{coverage.missingRegionCount > 0 ? ` · 지역 미입력 ${coverage.missingRegionCount}명` : ""}</p>
          <p>등록 시술 예: {coverage.services.length ? coverage.services.join(" · ") : "아직 등록된 시술이 없어요."}</p>
          <small>{dateTime(coverage.checkedAt)} 조회 · 디자이너가 공개한 정보이며, 등록 수는 선택한 날짜의 예약 가능 수와 달라요. {coverage.sourceUpdatedAt ? `공개 정보 마지막 수정: ${dateTime(coverage.sourceUpdatedAt)}` : ""}</small>
        </aside>
      )}
      {coverageError && <p className="matching-disclosure">공개 등록 범위를 불러오지 못했어요. 검색 결과와는 별도로 <button type="button" className="text-button" onClick={() => setCoverageAttempt((v) => v + 1)}>등록 범위 다시 확인</button></p>}
      <div className="matching-status" role="status" aria-live="polite">
        {busy ? "선택한 조건의 실제 빈 시간을 확인하고 있어요…" : searched && currentSearch ? `${currentSearch.region} · ${currentSearch.service} · ${currentSearch.date}: 예약 가능한 디자이너 ${items.length}명 · ${dateTime(checkedAt)} 확인` : !submitted ? "지역·시술·날짜를 고르면 예약 가능한 디자이너를 확인할 수 있어요." : ""}
      </div>
      {message && !busy && <button className="text-button" type="button" onClick={() => setRetry((v) => v + 1)}>같은 조건으로 다시 확인</button>}
      <section className="recommendation-grid" aria-busy={busy}>
        {items.map((item) => (
          <article className="panel" key={item.user_id}>
            <div className="recommendation-work">
              {item.matchedService.portfolioId ? (
                <img
                  src={`${API_URL}/api/v1/portfolio/assets/${item.matchedService.portfolioId}`}
                  alt={`${item.name} 디자이너의 ${item.matchedService.name} 작업`}
                />
              ) : null}
              <button
                type="button"
                className={favoriteSlugs.includes(item.slug) ? "favorite-button is-favorite" : "favorite-button"}
                aria-label={
                  favoriteSlugs.includes(item.slug) ? `${item.name} 디자이너 찜 해제` : `${item.name} 디자이너 찜하기`
                }
                onClick={() => void toggleFavorite(item)}
              >
                <Heart />
              </button>
            </div>
            <div className="recommendation-heading">
              <span className="match-score">조건 일치 {item.score}</span>
              <small>{item.region_label}</small>
              <h2>{item.name} 디자이너</h2>
              <p>{item.salon_name || item.bio || item.specialties.join(" · ")}</p>
            </div>
            <div className="recommendation-service">
              <span>
                <b>{item.matchedService.name}</b>
                <small>{item.matchedService.durationMinutes}분</small>
              </span>
              <strong>
                {item.matchedService.price === item.matchedService.priceMax
                  ? won(item.matchedService.price)
                  : `${won(item.matchedService.price)}–${won(item.matchedService.priceMax)}`}
              </strong>
            </div>
            <p className="earliest-slot">
              <CalendarDays /> {dateTime(item.earliest_slot)} 예약 가능
            </p>
            <ul>
              {item.reasons.map((reason) => (
                <li key={reason}>
                  <CheckCircle2 /> {reason}
                </li>
              ))}
            </ul>
            <button
              className="button button-full"
              onClick={() =>
                navigate(
                  `/designer/${item.slug}?serviceId=${encodeURIComponent(item.matchedService.id)}&date=${encodeURIComponent(item.earliest_slot.slice(0, 10))}`,
                )
              }
            >
              작업·후기·시간 보기
            </button>
          </article>
        ))}
        {searched && !items.length && (
          <section className="panel platform-empty">
            <Search />
            <h2>이 동네에 아직 공개한 디자이너가 없어요.</h2>
            <p>공개된 시술·가격과 선택한 날짜의 빈 시간을 함께 확인한 결과예요. 다른 날짜나 지역·시술로 직접 바꿔 볼 수 있어요.</p>
            <button className="button" type="button" onClick={editConditions}>날짜·검색 조건 바꾸기</button>
            <button className="text-button" onClick={() => { const url = `${window.location.origin}/ko/start`; if (navigator.share) void navigator.share({ title: "내 이름으로 예약받기", url }).catch(() => undefined); else void navigator.clipboard.writeText(url).then(() => setMessage("디자이너 초대 주소를 복사했어요.")); }}>아는 디자이너에게 알려 주기</button>
          </section>
        )}
      </section>
    </main>
  );
}

export function StyleAdvisorPage() {
  const accessToken = token();
  const [advice, setAdvice] = useState("");
  const [remaining, setRemaining] = useState<number | null>(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (accessToken)
      void api<{ remaining: number }>("/api/v1/ai/usage", {}, accessToken)
        .then((value) => setRemaining(value.remaining))
        .catch((error) => setMessage(errorMessage(error)));
  }, [accessToken]);
  if (!accessToken)
    return <LoginRequired text="AI 스타일 상담은 사용자별 비용 한도와 기록 보호를 위해 로그인이 필요합니다." />;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    const data = new FormData(event.currentTarget);
    try {
      const result = await api<{ advice: string; remaining: number }>(
        "/api/v1/ai/style-advice",
        { method: "POST", body: JSON.stringify(Object.fromEntries(data)) },
        accessToken,
      );
      setAdvice(result.advice);
      setRemaining(result.remaining);
    } catch (error) {
      setMessage(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }
  return (
    <main className="platform-page style-advisor-page">
      <section className="platform-hero">
        <p className="eyebrow">AI style advisor</p>
        <h1>사진을 저장하지 않는 스타일 상담</h1>
        <p>직접 선택한 얼굴형·모발·관리 선호만 사용하며 하루 5회로 비용을 제한합니다.</p>
      </section>
      <div className="platform-grid two">
        <form className="panel ai-form" onSubmit={submit}>
          <p>오늘 남은 추천 {remaining ?? "-"}회</p>
          <label>
            얼굴형
            <select name="faceShape">
              <option value="unknown">잘 모르겠어요</option>
              <option value="oval">계란형</option>
              <option value="round">둥근형</option>
              <option value="square">각진형</option>
              <option value="long">긴형</option>
              <option value="heart">하트형</option>
            </select>
          </label>
          <label>
            모발
            <select name="hairType">
              <option value="straight">직모</option>
              <option value="wavy">반곱슬</option>
              <option value="curly">곱슬</option>
              <option value="coily">강한 곱슬</option>
              <option value="unknown">잘 모르겠어요</option>
            </select>
          </label>
          <label>
            현재 길이
            <select name="currentLength">
              <option value="short">짧음</option>
              <option value="medium">중간</option>
              <option value="long">김</option>
            </select>
          </label>
          <label>
            관리 선호
            <select name="maintenance">
              <option value="low">간편</option>
              <option value="medium">보통</option>
              <option value="high">스타일링 가능</option>
            </select>
          </label>
          <label>
            원하는 변화
            <textarea
              name="goals"
              minLength={2}
              maxLength={500}
              placeholder="예: 얼굴 옆선을 자연스럽게 감싸고 손질이 쉬운 스타일"
              required
            />
          </label>
          {message && <p className="platform-message">{message}</p>}
          <button className="button" disabled={busy || remaining === 0}>
            <Bot /> {busy ? "추천 생성 중…" : "스타일 추천 받기"}
          </button>
        </form>
        <section className="panel ai-result">
          <Sparkles />
          {advice ? (
            <pre>{advice}</pre>
          ) : (
            <>
              <h2>추천 결과가 여기에 표시됩니다.</h2>
              <p>AI 결과는 참고용이며 실제 시술 전 디자이너와 상담하세요.</p>
            </>
          )}
        </section>
      </div>
    </main>
  );
}

interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
  status: string;
  designer_bookings: number;
  portfolio_count: number;
}
interface AdminListing {
  user_id: string;
  name: string;
  email: string;
  slug: string;
  salon_name: string | null;
  region_label: string | null;
  specialties: string;
  listing_status: string;
  listing_status_reason: string | null;
  is_published: number;
  active_services: number;
  public_work: number;
  public_reviews: number;
  updated_at: string;
}
interface AdminReview {
  id: string;
  rating: number;
  comment: string | null;
  status: string;
  dispute_note: string | null;
  created_at: string;
  designer_name: string;
  slug: string;
  service_name: string;
  open_reports: number;
}
interface AdminReviewReport {
  id: string;
  review_id: string;
  reason: string;
  detail: string | null;
  status: string;
  resolution: string | null;
  reporter_name: string;
  rating: number;
  comment: string | null;
  designer_name: string;
  created_at: string;
}
interface TrustOverview {
  users?: { total?: number; active?: number; suspended?: number };
  listings?: {
    total?: number;
    published?: number;
    needs_review?: number;
    suspended?: number;
  };
  bookings?: { total?: number; pending?: number; completed?: number };
  reviews?: {
    total?: number;
    published?: number;
    under_review?: number;
    hidden?: number;
  };
  reviewReports?: { total?: number; pending?: number; reviewing?: number };
}
interface AuditLog {
  id: string;
  action: string;
  target_type: string;
  target_id: string;
  created_at: string;
  admin_name: string;
}
export function AdminPage() {
  const accessToken = token();
  const [overview, setOverview] = useState<TrustOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [listings, setListings] = useState<AdminListing[]>([]);
  const [reviews, setReviews] = useState<AdminReview[]>([]);
  const [reports, setReports] = useState<AdminReviewReport[]>([]);
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [message, setMessage] = useState("");
  const load = useCallback(() => {
    if (!accessToken) return;
    void Promise.all([
      api<TrustOverview>("/api/v1/admin/overview", {}, accessToken),
      api<AdminUser[]>("/api/v1/admin/users", {}, accessToken),
      api<AdminListing[]>("/api/v1/admin/listings", {}, accessToken),
      api<AdminReview[]>("/api/v1/admin/reviews", {}, accessToken),
      api<AdminReviewReport[]>("/api/v1/admin/review-reports", {}, accessToken),
      api<AuditLog[]>("/api/v1/admin/audit-logs", {}, accessToken),
    ])
      .then(([summary, userRows, listingRows, reviewRows, reportRows, auditRows]) => {
        setOverview(summary);
        setUsers(userRows);
        setListings(listingRows);
        setReviews(reviewRows);
        setReports(reportRows);
        setLogs(auditRows);
      })
      .catch((error) => setMessage(errorMessage(error)));
  }, [accessToken]);
  useEffect(load, [load]);
  if (!accessToken) return <LoginRequired text="운영자 기능은 관리자 계정만 접근할 수 있습니다." />;
  async function mutate(work: () => Promise<unknown>, success: string) {
    setMessage("");
    try {
      await work();
      setMessage(success);
      load();
    } catch (error) {
      setMessage(errorMessage(error));
    }
  }
  async function changeUser(user: AdminUser) {
    const status = user.status === "active" ? "suspended" : "active";
    await mutate(
      () =>
        api(
          `/api/v1/admin/users/${user.id}/status`,
          {
            method: "PATCH",
            body: JSON.stringify({
              status,
              reason: "신뢰 운영 콘솔 수동 조치",
            }),
          },
          accessToken,
        ),
      status === "active" ? "사용자 접근을 복구했습니다." : "사용자 접근을 정지했습니다.",
    );
  }
  async function moderateListing(listing: AdminListing, status: "published" | "needs_review" | "suspended") {
    await mutate(
      () =>
        api(
          `/api/v1/admin/listings/${listing.user_id}/status`,
          {
            method: "PATCH",
            body: JSON.stringify({
              status,
              reason:
                status === "published"
                  ? "공개 요건과 프로필 정보를 확인했습니다."
                  : status === "needs_review"
                    ? "프로필 정보 재확인이 필요합니다."
                    : "신뢰 정책 검토에 따라 공개를 중단했습니다.",
            }),
          },
          accessToken,
        ),
      status === "published" ? "디자이너 프로필을 공개했습니다." : "프로필 공개 상태를 변경했습니다.",
    );
  }
  async function moderateReview(review: AdminReview, status: "published" | "under_review" | "hidden") {
    await mutate(
      () =>
        api(
          `/api/v1/admin/reviews/${review.id}/status`,
          {
            method: "PATCH",
            body: JSON.stringify({
              status,
              reason:
                status === "published"
                  ? "완료 예약과 후기 내용을 다시 확인했습니다."
                  : status === "under_review"
                    ? "당사자 이의 또는 신고 검토를 시작했습니다."
                    : "신뢰 정책 검토에 따라 후기를 숨겼습니다.",
            }),
          },
          accessToken,
        ),
      "검증 후기 상태를 변경했습니다.",
    );
  }
  async function resolveReviewReport(
    report: AdminReviewReport,
    status: "reviewing" | "resolved" | "dismissed",
    reviewStatus?: "published" | "under_review" | "hidden",
  ) {
    await mutate(
      () =>
        api(
          `/api/v1/admin/review-reports/${report.id}`,
          {
            method: "PATCH",
            body: JSON.stringify({
              status,
              reviewStatus,
              resolution:
                status === "reviewing"
                  ? "완료 예약과 당사자 의견을 대조하기 시작했습니다."
                  : status === "resolved"
                    ? "신고 내용을 확인하고 후기 상태 조치를 완료했습니다."
                    : "완료 예약과 제출 자료를 대조한 결과 숨김 근거가 충분하지 않습니다.",
            }),
          },
          accessToken,
        ),
      "후기 신고 처리를 기록했습니다.",
    );
  }
  return (
    <main className="platform-page admin-page">
      <SupportConsole token={accessToken}/>
      <section className="platform-hero">
        <p className="eyebrow">Operations</p>
        <h1>신뢰 운영 콘솔</h1>
        <p>디자이너 공개 품질과 완료 예약 후기, 당사자 이의·신고를 한 흐름에서 검토합니다.</p>
      </section>
      {message && <p className="platform-message">{message}</p>}
      {overview && (
        <div className="metric-grid">
          <article>
            <Users />
            <span>
              <small>전체 사용자</small>
              <b>{overview.users?.total || 0}</b>
            </span>
          </article>
          <article>
            <CheckCircle2 />
            <span>
              <small>공개 디자이너</small>
              <b>{overview.listings?.published || 0}</b>
            </span>
          </article>
          <article>
            <ShieldCheck />
            <span>
              <small>프로필 재검토</small>
              <b>{overview.listings?.needs_review || 0}</b>
            </span>
          </article>
          <article>
            <CalendarDays />
            <span>
              <small>완료 예약</small>
              <b>{overview.bookings?.completed || 0}</b>
            </span>
          </article>
          <article>
            <Heart />
            <span>
              <small>공개 검증 후기</small>
              <b>{overview.reviews?.published || 0}</b>
            </span>
          </article>
          <article>
            <MessageCircle />
            <span>
              <small>후기 신고 대기</small>
              <b>{overview.reviewReports?.pending || 0}</b>
            </span>
          </article>
        </div>
      )}
      <div className="platform-grid two">
        <section className="panel admin-list">
          <h2>디자이너 공개 품질</h2>
          {listings.map((listing) => (
            <div key={listing.user_id}>
              <span>
                <b>
                  {listing.name} 디자이너 · {listing.region_label || "지역 미입력"}
                </b>
                <small>
                  {listing.salon_name || "소속 미입력"} · {listing.listing_status} · 시술 {listing.active_services} ·
                  작업 {listing.public_work} · 후기 {listing.public_reviews}
                </small>
                {listing.listing_status_reason && <small>{listing.listing_status_reason}</small>}
              </span>
              <div className="admin-action-row">
                {listing.listing_status !== "published" && (
                  <button
                    disabled={listing.listing_status === "draft"}
                    onClick={() => void moderateListing(listing, "published")}
                  >
                    공개
                  </button>
                )}
                <button onClick={() => void moderateListing(listing, "needs_review")}>재검토</button>
                <button onClick={() => void moderateListing(listing, "suspended")}>중단</button>
              </div>
            </div>
          ))}
          {!listings.length && <p className="muted">검토할 디자이너 프로필이 없습니다.</p>}
        </section>
        <section className="panel admin-list">
          <h2>완료 예약 검증 후기</h2>
          {reviews.map((review) => (
            <div key={review.id}>
              <span>
                <b>
                  ★ {review.rating} · {review.designer_name} · {review.service_name}
                </b>
                <small>
                  {review.status} · 열린 신고 {review.open_reports}건 · {dateTime(review.created_at)}
                </small>
                {review.comment && <small>{review.comment}</small>}
                {review.dispute_note && <small>디자이너 이의: {review.dispute_note}</small>}
              </span>
              <div className="admin-action-row">
                <button onClick={() => void moderateReview(review, "published")}>공개</button>
                <button onClick={() => void moderateReview(review, "under_review")}>검토</button>
                <button onClick={() => void moderateReview(review, "hidden")}>숨김</button>
              </div>
            </div>
          ))}
          {!reviews.length && <p className="muted">등록된 검증 후기가 없습니다.</p>}
        </section>
      </div>
      <section className="panel admin-list report-admin-list">
        <h2>후기 신고·이의 처리함</h2>
        {reports.map((report) => (
          <div key={report.id}>
            <span>
              <b>
                {report.designer_name} · ★ {report.rating} · {report.reason}
              </b>
              <small>
                {report.reporter_name} · {report.status} · {dateTime(report.created_at)}
              </small>
              {report.comment && <small>후기: {report.comment}</small>}
              {report.detail && <small>{report.detail}</small>}
            </span>
            <div className="admin-action-row">
              {report.status === "pending" && (
                <button onClick={() => void resolveReviewReport(report, "reviewing", "under_review")}>검토 시작</button>
              )}
              {!["resolved", "dismissed"].includes(report.status) && (
                <>
                  <button onClick={() => void resolveReviewReport(report, "resolved", "hidden")}>숨김·완료</button>
                  <button onClick={() => void resolveReviewReport(report, "dismissed", "published")}>공개 유지</button>
                </>
              )}
            </div>
          </div>
        ))}
        {!reports.length && <p className="muted">접수된 후기 신고가 없습니다.</p>}
      </section>
      <section className="panel admin-list account-admin-list">
        <h2>계정 접근 관리</h2>
        {users.map((user) => (
          <div key={user.id}>
            <span>
              <b>{user.name}</b>
              <small>
                {user.email} · {user.role} · {user.status}
              </small>
            </span>
            {user.role !== "admin" && (
              <button onClick={() => void changeUser(user)}>
                {user.status === "active" ? "접근 정지" : "접근 복구"}
              </button>
            )}
          </div>
        ))}
      </section>
      <section className="panel admin-list">
        <h2>감사 로그</h2>
        {logs.map((log) => (
          <div key={log.id}>
            <span>
              <b>{log.action}</b>
              <small>
                {log.admin_name} · {log.target_type}/{log.target_id} · {dateTime(log.created_at)}
              </small>
            </span>
          </div>
        ))}
      </section>
    </main>
  );
}
